import * as React from 'react';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import type { IntlShape } from 'react-intl';
import {
  Combobox,
  Tooltip,
  Text,
  Select,
  SELECT_EMPTY_VALUE,
} from '@box/blueprint-web';
import isUndefined from 'lodash/isUndefined';
import useId from '../../utils/useId';
import messages from './messages';
import css from './blueprint-select-field.module.scss';
import type { SelectFieldProps } from './select-field';
import { BuilderInlineError } from '../../../components/new-file-request-builder/new-form-element/builder-inline-error';

type Props = SelectFieldProps & {
  intl: IntlShape;
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const onFilter = (query: any, option: any) =>
  option.text.toLowerCase().includes(query.toLowerCase());

const BlueprintSelectField = (props: Props) => {
  const {
    className,
    error,
    isRequired,
    isDisabled,
    isMultiple,
    onChange,
    options,
    selectedValues,
    intl,
    label,
    infoText,
    description,
    maximumSelections,
    isSubmitter,
  } = props;

  const placeholder =
    isMultiple ||
    (typeof maximumSelections === 'number' &&
      (maximumSelections === 0 || maximumSelections > 1))
      ? intl.formatMessage(messages.multiSelectFieldPlaceholderMessage)
      : intl.formatMessage(messages.singleSelectFieldPlaceholderMessage);

  // radix-ui Select.Item doesn't accept empty string anymore.
  // Filter them to prevent crash
  const filteredOptions = options.filter(option => option !== '');
  const optionObjects = filteredOptions.map(option => ({
    text: option,
    value: option,
  }));

  const descriptionID = useId('description');
  const labelID = useId('labelID');
  const ariaAttrs = {
    'aria-describedby': description ? descriptionID : undefined,
    'aria-labelledby': labelID,
  } as const;

  const handleMultiSelectChange = (
    selectedOptions: Array<{
      text: string;
      value?: string;
    }>,
  ) => {
    const filteredSelectedOptions = selectedOptions.filter(
      option => !isUndefined(option?.value),
    );

    onChange(filteredSelectedOptions);
  };

  const handleSingleSelectChange = (value: string) => {
    onChange(value === SELECT_EMPTY_VALUE ? [] : [{ value }]);
  };

  return (
    <div className={classNames(className, css.blueprintSelectContainer)}>
      <Text
        id={labelID}
        as="label"
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
        color="text-on-light-default"
        variant="subtitle"
        className={classNames({ [css.label]: isSubmitter })}
      >
        {label}
      </Text>
      <Tooltip
        content={infoText}
        // Hide tooltip when no info text provided
        open={infoText ? undefined : false}
        side="right"
        variant="standard"
        __checkInteractivity={!isMultiple}
      >
        {isMultiple ? (
          /*
            Added additional div to make the width of options list 
            same as the width of Combobox with multiselected options

            To fix a11y warning the flag __checkInteractivity is added to Tooltip
          */
          <div>
            <Combobox
              as="input"
              data-testid="blueprint-multiselect-field"
              disabled={isDisabled}
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
              displayValue={o => o.text}
              error={error}
              filterFn={onFilter}
              freeInput={false}
              multiselect
              noResultMessage={intl.formatMessage(messages.noResultMessage)}
              // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
              onValueChange={handleMultiSelectChange}
              options={optionObjects}
              placeholder={placeholder}
              required={isRequired}
              value={selectedValues}
              {...ariaAttrs}
            />
          </div>
        ) : (
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          <Select
            data-testid="blueprint-singleselect-field"
            disabled={isDisabled}
            error={error}
            onValueChange={handleSingleSelectChange}
            placeholder={placeholder}
            required={isRequired}
            value={selectedValues[0] || ''}
            {...ariaAttrs}
          >
            <Select.Content className={css.customSelectContent}>
              <Select.Option
                text={intl.formatMessage(
                  messages.singleSelectFieldPlaceholderMessage,
                )}
                value={SELECT_EMPTY_VALUE}
              />
              {filteredOptions.map(option => (
                <Select.Option key={option} text={option} value={option} />
              ))}
            </Select.Content>
          </Select>
        )}
      </Tooltip>

      {description && (
        <Text
          as="span"
          className={css.blueprintDescriptionText}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          color="textOnLight"
          id={descriptionID}
          variant="bodyDefault"
        >
          {description}
        </Text>
      )}

      <BuilderInlineError />
    </div>
  );
};

BlueprintSelectField.displayName = 'BlueprintSelectField';

export type BlueprintSelectFieldProps = Props;
export { BlueprintSelectField };
type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(BlueprintSelectField);
export default Wrapped as React.ComponentType<WrappedProps>;
